/**
 * render.ts — PortableSession -> non-native resume package (codex / cursor path).
 *
 * Ported from the validated prototype (`emit/_shared.mjs` + `emit/nonnative.mjs`).
 * Produces the two artifacts the proven codex/cursor launch commands read:
 *   RESUME.md    — human+LLM readable, space-budgeted
 *   resume.json  — the full PortableSession, verbatim, in a tiny envelope
 *
 * The prototype's CLI wrapper (arg parsing, file writing, launch-command printing)
 * lives in fork.ts now; this module is pure string-in / strings-out.
 */

import type {
  PortableSession,
  PortableSessionMeta,
  TimelineEntry,
  TodoItem,
  Subagent,
  FileTouched,
  Decision,
} from "./extractor.js";
import { renderResumeContext } from "./resume-context.js";

// ── Action Log truncation budget ─────────────────────────────────────────────
const HEAD_BYTES = 2048; // 2 KB head
const TAIL_BYTES = 1024; // 1 KB tail

const isoNow = (): string => new Date().toISOString().replace(/\.\d{3}Z$/, "Z");

// ---------------------------------------------------------------------------
// RESUME.md rendering (from nonnative.mjs)
// ---------------------------------------------------------------------------

function oneLineGoal(goal: string | undefined): string {
  if (!goal) return "resumed session";
  const first = String(goal).split(/(?<=\.)\s/)[0].trim();
  return first.replace(/\.$/, "");
}

// Truncate a large non-error tool output to head+tail with an elision marker.
// Errors are ALWAYS kept whole; the full output stays verbatim in resume.json.
function budget(text: string | undefined, isError: boolean): string {
  const s = text ?? "";
  if (isError) return s;
  const buf = Buffer.from(s, "utf8");
  if (buf.length <= HEAD_BYTES + TAIL_BYTES) return s;
  const head = buf.subarray(0, HEAD_BYTES).toString("utf8");
  const tail = buf.subarray(buf.length - TAIL_BYTES).toString("utf8");
  const elided = buf.length - HEAD_BYTES - TAIL_BYTES;
  return `${head}\n… [${elided} bytes elided — full output in resume.json] …\n${tail}`;
}

function fence(body: string | undefined, lang = ""): string {
  return "```" + lang + "\n" + (body ?? "") + "\n```";
}

// Pair each tool_call with its tool_result by tool_use_id (unique across a
// session; the extractor also guarantees the result is emitted right after its call).
function resultFor(call: TimelineEntry, timeline: TimelineEntry[]): TimelineEntry | undefined {
  const id = call.tool?.id;
  if (!id) return undefined;
  return timeline.find((e) => e.kind === "tool_result" && e.result?.tool_use_id === id);
}

function readRange(input: any): string {
  const off = input?.offset;
  const lim = input?.limit;
  if (off != null && lim != null) return `:${off}-${off + lim - 1}`;
  if (off != null) return `:${off}-`;
  return "";
}

function editDiff(input: any): string {
  const del = String(input?.old_string ?? "")
    .split("\n")
    .map((l) => `- ${l}`);
  const add = String(input?.new_string ?? "")
    .split("\n")
    .map((l) => `+ ${l}`);
  return [...del, ...add].join("\n");
}

// ── Action Log: walk seq order, pair calls with results, render verbatim ──
function renderActionLog(timeline: TimelineEntry[]): string {
  const ordered = [...timeline].sort((a, b) => a.seq - b.seq || 0);
  const out: string[] = [];

  for (const e of ordered) {
    if (e.kind === "thinking") continue; // reasoning traces are lost by design
    if (e.kind === "tool_result") continue; // rendered with its call
    if (e.kind === "text") {
      if (e.role !== "assistant") continue; // user turn → Task Goal, not the log
      const note = String(e.text ?? "")
        .split("\n")
        .map((l) => `> ${l}`)
        .join("\n");
      out.push(`> note: ${note.replace(/^> /, "")}`);
      continue;
    }
    if (e.kind !== "tool_call") continue;

    const name = e.tool?.name ?? "Tool";
    const input = e.tool?.input ?? {};
    const res = resultFor(e, ordered);
    const result = res?.result ?? {};
    const exitCode = result?.structured?.exitCode;
    const isError = !!result.is_error || (typeof exitCode === "number" && exitCode !== 0);

    if (name === "Bash") {
      const cmd = input.command ?? "";
      const body =
        result.content ??
        [result?.structured?.stdout, result?.structured?.stderr].filter(Boolean).join("\n");
      const status =
        (typeof exitCode === "number" ? `exit ${exitCode}` : isError ? "exit nonzero" : "exit 0") +
        (isError ? " — ERROR (kept in full)" : "");
      out.push(`#${e.seq} [Bash]\n${fence(cmd, "bash")}\n→ ${status}\n${fence(budget(body, isError))}`);
      continue;
    }

    if (name === "Read") {
      const path = input.file_path ?? input.path ?? "";
      const header = `#${e.seq} [Read]  ${path}${readRange(input)}`;
      if (result.stale) {
        out.push(`${header}\n→ [STALE — file changed after this read; RE-READ before relying on it]`);
      } else {
        out.push(
          `${header}\n→ (snapshot omitted — file current on disk; re-read if needed. Full copy in resume.json)`
        );
      }
      continue;
    }

    if (name === "Edit" || name === "MultiEdit") {
      const path = input.file_path ?? input.path ?? "";
      out.push(
        `#${e.seq} [${name}]  ${path}\n${fence(editDiff(input), "diff")}\n→ [APPLIED — already on disk; DO NOT re-apply]`
      );
      continue;
    }

    if (name === "Write") {
      const path = input.file_path ?? input.path ?? "";
      const content = budget(String(input.content ?? ""), false);
      out.push(
        `#${e.seq} [Write]  ${path}\n${fence(content)}\n→ [APPLIED — already on disk; DO NOT re-apply]`
      );
      continue;
    }

    // Generic tool: render input + result body.
    const body = result.content ?? JSON.stringify(result.structured ?? {}, null, 2);
    out.push(
      `#${e.seq} [${name}]\n${fence(JSON.stringify(input, null, 2), "json")}\n→ ${
        isError ? "ERROR" : "ok"
      }\n${fence(budget(body, isError))}`
    );
  }

  return out.join("\n\n");
}

function renderTodo(todo: TodoItem[] = []): string {
  if (!todo.length) return "_(none)_";
  return todo
    .map((t) => {
      const box = t.status === "completed" ? "x" : " ";
      const tag = t.status === "in_progress" ? "  _(in progress)_" : "";
      return `- [${box}] ${t.content}${tag}`;
    })
    .join("\n");
}

function renderFiles(files: FileTouched[] = []): string {
  if (!files.length) return "_(none)_";
  const rows = files.map((f) => {
    const ops = (f.ops ?? []).join(", ");
    const mutating = (f.ops ?? []).some((o) => o === "edit" || o === "write");
    const state = mutating ? "APPLIED (on disk)" : "read only";
    return `| ${f.path} | ${ops} | ${state} |`;
  });
  return ["| path | ops | state |", "|------|-----|-------|", ...rows].join("\n");
}

function renderDecisions(decisions: Decision[] = []): string {
  if (!decisions.length) return "_(none)_";
  return decisions
    .map((d) => {
      const rej = (d.rejected_alternatives ?? []).length
        ? ` Rejected: ${d.rejected_alternatives!.join("; ")}.`
        : "";
      return `- **${d.what}**${d.why ? " — " + d.why : ""}${rej}`;
    })
    .join("\n");
}

function renderSubagents(subagents: Subagent[] = []): string {
  if (!subagents.length) return "_(none)_";
  return subagents
    .map((s) => `- **${s.task}** (${s.tool_calls_count ?? "?"} tool calls): ${s.conclusion}`)
    .join("\n");
}

function renderList(items: string[] = []): string {
  if (!items.length) return "_(none)_";
  return items.map((q) => `- ${q}`).join("\n");
}

function renderMd(session: PortableSession, orientation?: string): string {
  const m = session.meta ?? ({} as PortableSessionMeta);
  const resumeCtx = renderResumeContext(session);
  // The fork's worktree/branch interview, surfaced as a prominent top-of-doc
  // section (not buried in the Action Log) so the resuming agent actually offers
  // the choice before touching code. Empty when fork.ts passes no orientation.
  const branchSection = orientation
    ? `## Branch & Worktree — do this BEFORE any code change\n\n` +
      `Before making any code changes, present the choice below to the user and let them decide how to ` +
      `work. Do NOT switch branches, stash, or create a worktree without their say-so.\n\n${orientation}\n\n`
    : "";
  const dropped = session.dropped ?? ({} as PortableSession["dropped"]);
  const droppedNote = [
    dropped.thinking_signatures ? `${dropped.thinking_signatures} thinking-trace(s) dropped` : null,
    dropped.images ? `${dropped.images} image(s) dropped` : null,
    dropped.compacted_turns ? `${dropped.compacted_turns} compacted turn(s)` : null,
  ]
    .filter(Boolean)
    .join("; ");

  const fm = [
    "---",
    "resume_artifact_version: 1",
    `source_agent: ${m.source_agent ?? "unknown"}`,
    `source_agent_version: ${m.source_agent_version ?? "unknown"}`,
    `source_session_id: ${m.source_session_id ?? "unknown"}`,
    `generated_at: ${isoNow()}`,
    `started_at: ${m.started_at ?? "unknown"}`,
    `ended_at: ${m.ended_at ?? "unknown"}`,
    `cwd: ${m.cwd ?? ""}`,
    `git_branch: ${m.git_branch ?? "unknown"}`,
    `git_head: ${m.git_head ?? "unknown"}`,
    `git_dirty: ${m.git_dirty ?? "unknown"}`,
    "sidecar: ./resume.json",
    "---",
  ].join("\n");

  return `${fm}

# RESUME — ${oneLineGoal(m.task_goal)}

## READ ME FIRST  (contract for the resuming agent)

You are RESUMING a **Sailfish Autofix** session that a prior agent already worked on.
This document is the record of what it did.

1. **The edits described under "Files Touched" and marked \`[APPLIED]\` in the Action Log
   are ALREADY IN THE WORKING TREE. Do NOT re-apply them.** Re-read the current files
   before you act.
2. **Tool outputs below are historical snapshots.** Reads flagged \`[STALE]\` may no longer
   match disk — re-read them. Command outputs describe what happened then, not now.
3. **Your job:** start from the "Resume Context" block below (task → last action →
   blocker → next step). Don't redo completed TODO items. Don't re-investigate
   questions the Action Log already answered.
4. If you need an exact tool output that was truncated here, read \`./resume.json\`.
   (The worktree-drift caveat is in the Resume Context block below.)

${branchSection}## Task Goal
${m.task_goal ?? "(none recorded)"}

## Resume Context
${resumeCtx || "_(none recorded)_"}

## TODO
${renderTodo(session.todo)}

## Action Log  (verbatim; ordered by seq)

${renderActionLog(session.timeline ?? [])}

## Files Touched
${renderFiles(session.files_touched)}

## Decisions & Rejected Alternatives
${renderDecisions(session.decisions)}

## Subagent Conclusions
${renderSubagents(session.subagents)}

## Open Questions
${renderList(session.open_questions)}

---
_Generated from a \`PortableSession\`. Fidelity caveats: no model thinking-traces${
    droppedNote ? ` (${droppedNote})` : ""
  }, tool outputs space-budgeted (full copy in resume.json), a different model is reading
this than produced it. See resume.json for the complete record._
`;
}

// resume.json envelope: full PortableSession, verbatim, minus the top-level
// _schema/_version which move to the envelope.
function renderSidecar(portable: PortableSession): string {
  const { _schema, _version, ...session } = portable;
  return JSON.stringify(
    { _schema: _schema ?? "portable-session", _version: _version ?? 1, session },
    null,
    2
  );
}

/**
 * Render a PortableSession into the non-native resume package (codex / cursor).
 * Returns the two file bodies keyed by their on-disk basenames; the caller
 * writes them into the target repo.
 */
export function renderNonNative(
  session: PortableSession,
  orientation?: string
): { "RESUME.md": string; "resume.json": string } {
  return {
    "RESUME.md": renderMd(session, orientation),
    "resume.json": renderSidecar(session),
  };
}
